import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { X, Mic, Check, GraduationCap, Users, Volume2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Iter-27.60 — Modal d'assignation bulk d'une voix ElevenLabs à plusieurs modules.
 *
 * Évite à l'admin d'éditer chaque module un par un quand il veut appliquer la
 * même voix sur 5/10/15 modules d'un parcours.
 *
 * Steps :
 *   1. Choisir une voix (liste FR ElevenLabs + custom du compte)
 *   2. Ajuster la vitesse (optionnel — décocher pour ne pas toucher au champ)
 *   3. Cocher les modules ciblés (groupés Recruteur / Référent)
 *   4. Apply → POST /api/admin/modules/bulk-voice
 */
export default function BulkVoiceModal({ onClose, onApplied }) {
    const [voices, setVoices] = useState([]);
    const [allModules, setAllModules] = useState([]);
    const [loadingVoices, setLoadingVoices] = useState(true);

    const [voiceId, setVoiceId] = useState("");
    const [speedEnabled, setSpeedEnabled] = useState(false);
    const [speed, setSpeed] = useState(1.1);
    const [selectedIds, setSelectedIds] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        api.get("/admin/tts/voices")
            .then((r) => setVoices(r.data?.voices || []))
            .finally(() => setLoadingVoices(false));
        api.get("/modules").then((r) => setAllModules(r.data));
    }, []);

    const { recruiterMods, managerMods } = useMemo(() => {
        const sorted = [...allModules].sort((a, b) => (a.order || 0) - (b.order || 0));
        return {
            recruiterMods: sorted.filter((m) => (m.audience || "recruiter") === "recruiter"),
            managerMods: sorted.filter((m) => m.audience === "manager"),
        };
    }, [allModules]);

    const allIds = useMemo(
        () => [...recruiterMods, ...managerMods].map((m) => m.module_id),
        [recruiterMods, managerMods],
    );
    const allSelected = allIds.length > 0 && selectedIds.length === allIds.length;

    const toggleModule = (mid) => {
        setSelectedIds((prev) =>
            prev.includes(mid) ? prev.filter((x) => x !== mid) : [...prev, mid],
        );
    };

    const selectGroup = (mods) => {
        const ids = mods.map((m) => m.module_id);
        const allInGroup = ids.every((id) => selectedIds.includes(id));
        setSelectedIds((prev) => {
            if (allInGroup) return prev.filter((id) => !ids.includes(id));
            const merged = new Set([...prev, ...ids]);
            return Array.from(merged);
        });
    };

    const submit = async () => {
        if (selectedIds.length === 0) {
            toast.error("Choisis au moins 1 module");
            return;
        }
        setSubmitting(true);
        try {
            const res = await api.post("/admin/modules/bulk-voice", {
                module_ids: selectedIds,
                tts_voice_id: voiceId || null,
                tts_speed: speedEnabled ? speed : null,
            });
            const n = res.data?.applied ?? selectedIds.length;
            const voiceName = voiceId
                ? voices.find((v) => v.voice_id === voiceId)?.name || "voix sélectionnée"
                : "voix admin globale (hérité)";
            toast.success(
                `${voiceName} appliquée à ${n} module${n > 1 ? "s" : ""}`,
            );
            onApplied?.();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        } finally {
            setSubmitting(false);
        }
    };

    const selectedVoice = voices.find((v) => v.voice_id === voiceId);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="bulk-voice-modal"
        >
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 20, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-shariti-black border border-white/10 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
                <div className="sticky top-0 bg-shariti-black/95 backdrop-blur border-b border-white/5 px-6 py-4 flex items-center justify-between z-10">
                    <div>
                        <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-shariti-orange-light">
                            Assignation en masse
                        </p>
                        <h2 className="text-xl font-extrabold text-white inline-flex items-center gap-2">
                            <Mic className="w-5 h-5 text-shariti-yellow" />
                            Voix du formateur
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="bulk-voice-close"
                        className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-white/70"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-6">
                    {/* Step 1 — Voix */}
                    <div>
                        <label className="text-[10px] font-thin tracking-[0.22em] uppercase text-white/45 mb-2 block">
                            1 · Voix à appliquer
                        </label>
                        {loadingVoices ? (
                            <p className="text-white/45 text-sm">Chargement des voix ElevenLabs…</p>
                        ) : (
                            <div
                                className="bg-white/3 border border-white/10 rounded-2xl max-h-56 overflow-y-auto p-1.5"
                                data-testid="bulk-voice-picker"
                            >
                                <button
                                    onClick={() => setVoiceId("")}
                                    data-testid="bulk-voice-option-inherit"
                                    className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-bold transition-colors inline-flex items-center justify-between ${
                                        voiceId === ""
                                            ? "bg-shariti-orange/15 text-white"
                                            : "text-white/70 hover:bg-white/5 hover:text-white"
                                    }`}
                                >
                                    <span>— Hériter de la voix admin globale —</span>
                                    {voiceId === "" && <Check className="w-4 h-4 text-shariti-orange" />}
                                </button>
                                {voices.map((v) => (
                                    <button
                                        key={v.voice_id}
                                        onClick={() => setVoiceId(v.voice_id)}
                                        data-testid={`bulk-voice-option-${v.voice_id}`}
                                        className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-bold transition-colors inline-flex items-center justify-between ${
                                            voiceId === v.voice_id
                                                ? "bg-shariti-orange/15 text-white"
                                                : "text-white/85 hover:bg-white/5 hover:text-white"
                                        }`}
                                    >
                                        <span className="truncate flex items-center gap-2">
                                            {v.is_custom && (
                                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-shariti-yellow/20 text-shariti-yellow font-extrabold">
                                                    ✦ CLONÉE
                                                </span>
                                            )}
                                            {v.name}
                                            {v.gender && (
                                                <span className="text-white/45 font-light">· {v.gender}</span>
                                            )}
                                            {v.descriptive && (
                                                <span className="text-white/45 font-light">· {v.descriptive}</span>
                                            )}
                                        </span>
                                        {voiceId === v.voice_id && <Check className="w-4 h-4 text-shariti-orange shrink-0" />}
                                    </button>
                                ))}
                            </div>
                        )}
                        {selectedVoice && (
                            <p className="text-[11px] text-white/50 mt-2 inline-flex items-center gap-1.5">
                                <Volume2 className="w-3 h-3" />
                                <span className="font-bold text-white/75">{selectedVoice.name}</span>
                                {" "}sera la nouvelle voix de ces modules.
                            </p>
                        )}
                    </div>

                    {/* Step 2 — Vitesse optionnelle */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-[10px] font-thin tracking-[0.22em] uppercase text-white/45">
                                2 · Vitesse (optionnel)
                            </label>
                            <label className="inline-flex items-center gap-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={speedEnabled}
                                    onChange={(e) => setSpeedEnabled(e.target.checked)}
                                    data-testid="bulk-voice-speed-toggle"
                                    className="accent-shariti-orange"
                                />
                                <span className="text-[11px] text-white/55 font-bold">
                                    Modifier la vitesse aussi
                                </span>
                            </label>
                        </div>
                        {speedEnabled && (
                            <div className="bg-white/3 border border-white/10 rounded-2xl p-3 flex items-center gap-3">
                                <input
                                    type="range"
                                    min="0.7"
                                    max="1.3"
                                    step="0.05"
                                    value={speed}
                                    onChange={(e) => setSpeed(parseFloat(e.target.value))}
                                    data-testid="bulk-voice-speed-slider"
                                    className="flex-1 accent-shariti-orange"
                                />
                                <span className="text-sm font-extrabold text-shariti-orange-light tabular-nums w-12 text-right">
                                    {speed.toFixed(2)}×
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Step 3 — Modules ciblés */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-[10px] font-thin tracking-[0.22em] uppercase text-white/45">
                                3 · Modules ciblés
                            </label>
                            <div className="flex items-center gap-2">
                                <span
                                    className="text-[10px] font-extrabold text-shariti-orange-light tabular-nums"
                                    data-testid="bulk-voice-selected-count"
                                >
                                    {selectedIds.length} sélectionné{selectedIds.length > 1 ? "s" : ""}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setSelectedIds(allSelected ? [] : [...allIds])}
                                    data-testid="bulk-voice-toggle-all"
                                    className="text-[10px] font-bold uppercase tracking-wider text-white/55 hover:text-white px-2 py-1 rounded-lg hover:bg-white/5"
                                >
                                    {allSelected ? "Tout désélectionner" : "Tout sélectionner"}
                                </button>
                            </div>
                        </div>
                        <div className="bg-white/3 border border-white/10 rounded-2xl max-h-72 overflow-y-auto">
                            {recruiterMods.length > 0 && (
                                <div>
                                    <div className="sticky top-0 bg-shariti-black/95 backdrop-blur border-b border-white/5 px-4 py-2 flex items-center justify-between gap-2">
                                        <span className="inline-flex items-center gap-2">
                                            <GraduationCap className="w-3.5 h-3.5 text-shariti-orange-light" />
                                            <span className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-shariti-orange-light">
                                                Parcours Recruteur ({recruiterMods.length})
                                            </span>
                                        </span>
                                        <button
                                            onClick={() => selectGroup(recruiterMods)}
                                            data-testid="bulk-voice-select-recruiter-group"
                                            className="text-[9px] font-bold uppercase tracking-wider text-white/55 hover:text-white px-2 py-0.5 rounded hover:bg-white/5"
                                        >
                                            Tout
                                        </button>
                                    </div>
                                    <div className="p-1.5">
                                        {recruiterMods.map((m) => (
                                            <ModuleRow
                                                key={m.module_id}
                                                module={m}
                                                selected={selectedIds.includes(m.module_id)}
                                                onToggle={() => toggleModule(m.module_id)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                            {managerMods.length > 0 && (
                                <div>
                                    <div className="sticky top-0 bg-shariti-black/95 backdrop-blur border-b border-white/5 px-4 py-2 flex items-center justify-between gap-2">
                                        <span className="inline-flex items-center gap-2">
                                            <Users className="w-3.5 h-3.5 text-shariti-yellow" />
                                            <span className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-shariti-yellow">
                                                Parcours Référent ({managerMods.length})
                                            </span>
                                        </span>
                                        <button
                                            onClick={() => selectGroup(managerMods)}
                                            data-testid="bulk-voice-select-manager-group"
                                            className="text-[9px] font-bold uppercase tracking-wider text-white/55 hover:text-white px-2 py-0.5 rounded hover:bg-white/5"
                                        >
                                            Tout
                                        </button>
                                    </div>
                                    <div className="p-1.5">
                                        {managerMods.map((m) => (
                                            <ModuleRow
                                                key={m.module_id}
                                                module={m}
                                                selected={selectedIds.includes(m.module_id)}
                                                onToggle={() => toggleModule(m.module_id)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <button
                        onClick={submit}
                        disabled={selectedIds.length === 0 || submitting}
                        data-testid="bulk-voice-submit"
                        className="w-full bg-shariti-yellow text-shariti-black font-extrabold py-3 rounded-2xl hover:bg-shariti-yellow/90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {submitting
                            ? "Application…"
                            : selectedIds.length === 0
                            ? "Choisis au moins un module"
                            : `Appliquer à ${selectedIds.length} module${selectedIds.length > 1 ? "s" : ""}`}
                    </button>
                </div>
            </motion.div>
        </motion.div>
    );
}

function ModuleRow({ module, selected, onToggle }) {
    return (
        <button
            type="button"
            onClick={onToggle}
            data-testid={`bulk-voice-module-row-${module.module_id}`}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                selected
                    ? "bg-shariti-orange/15 text-white"
                    : "text-white/85 hover:bg-white/5 hover:text-white"
            }`}
        >
            <div
                className={`shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
                    selected
                        ? "bg-shariti-orange border-shariti-orange"
                        : "bg-transparent border-white/25"
                }`}
            >
                {selected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
            </div>
            <div className="flex-1 min-w-0 flex items-center gap-2">
                <span className="text-sm font-bold truncate">{module.title}</span>
                {module.tts_voice_id && (
                    <span
                        className="text-[9px] font-bold uppercase tracking-wider text-white/45 shrink-0"
                        title="Ce module a déjà une voix custom — elle sera remplacée."
                    >
                        · voix custom
                    </span>
                )}
            </div>
        </button>
    );
}
